/**
 * Excel-style key map of the grid (pure). The component turns key events into these actions.
 */
export interface GridKey {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}

export type FillDir = 'down' | 'up' | 'left' | 'right';

export type GridAction =
  | { type: 'move'; dRow: number; dCol: number; extend: boolean }
  /** ⌘/Ctrl+arrow: jump to the edge. */
  | { type: 'edge'; dRow: number; dCol: number; extend: boolean }
  | { type: 'home'; extend: boolean; grid: boolean }
  | { type: 'end'; extend: boolean; grid: boolean }
  | { type: 'page'; dir: 1 | -1; extend: boolean }
  | { type: 'tab'; back: boolean }
  | { type: 'enter'; back: boolean }
  | { type: 'edit' }
  | { type: 'type'; text: string }
  | { type: 'clear' }
  | { type: 'space' }
  | { type: 'selectAll' }
  | { type: 'fill'; dir: FillDir }
  | { type: 'menu' }
  | { type: 'escape' }
  | { type: 'none' };

const ARROWS: Record<string, [number, number]> = {
  ArrowUp: [-1, 0],
  ArrowDown: [1, 0],
  ArrowLeft: [0, -1],
  ArrowRight: [0, 1],
};

/** Key → action while navigating (not editing). `mac` selects ⌘ as primary modifier. */
export function navAction(e: GridKey, mac: boolean): GridAction {
  const mod = mac ? e.metaKey : e.ctrlKey;
  const other = mac ? e.ctrlKey : e.metaKey;
  const arrow = ARROWS[e.key];
  if (arrow && !e.altKey && !other) {
    const [dRow, dCol] = arrow;
    return mod
      ? { type: 'edge', dRow, dCol, extend: e.shiftKey }
      : { type: 'move', dRow, dCol, extend: e.shiftKey };
  }
  if (other || e.altKey) return { type: 'none' };
  if (mod) {
    const k = e.key.toLowerCase();
    if (k === 'a' && !e.shiftKey) return { type: 'selectAll' };
    if (k === 'd' && !e.shiftKey) return { type: 'fill', dir: 'down' };
    if (k === 'r' && !e.shiftKey) return { type: 'fill', dir: 'right' };
    if (e.key === 'Home') return { type: 'home', extend: e.shiftKey, grid: true };
    if (e.key === 'End') return { type: 'end', extend: e.shiftKey, grid: true };
    return { type: 'none' };
  }
  switch (e.key) {
    case 'Tab':
      return { type: 'tab', back: e.shiftKey };
    case 'Enter':
      return e.shiftKey ? { type: 'enter', back: true } : { type: 'edit' };
    case 'F2':
      return { type: 'edit' };
    case 'Delete':
    case 'Backspace':
      return { type: 'clear' };
    case ' ':
      return e.shiftKey ? { type: 'none' } : { type: 'space' };
    case 'Home':
      return { type: 'home', extend: e.shiftKey, grid: false };
    case 'End':
      return { type: 'end', extend: e.shiftKey, grid: false };
    case 'PageDown':
      return { type: 'page', dir: 1, extend: e.shiftKey };
    case 'PageUp':
      return { type: 'page', dir: -1, extend: e.shiftKey };
    case 'F10':
      return e.shiftKey ? { type: 'menu' } : { type: 'none' };
    case 'ContextMenu':
      return { type: 'menu' };
    case 'Escape':
      return { type: 'escape' };
  }
  if (e.key.length === 1) return { type: 'type', text: e.key };
  return { type: 'none' };
}

export type EditKeyAction =
  { type: 'commit'; dRow: number; dCol: number } | { type: 'cancel' } | { type: 'none' };

/** Key → action inside a cell editor (text-like editors). */
export function editAction(e: GridKey): EditKeyAction {
  if (e.altKey || e.ctrlKey || e.metaKey) return { type: 'none' };
  if (e.key === 'Enter') return { type: 'commit', dRow: e.shiftKey ? -1 : 1, dCol: 0 };
  if (e.key === 'Tab') return { type: 'commit', dRow: 0, dCol: e.shiftKey ? -1 : 1 };
  if (e.key === 'Escape') return { type: 'cancel' };
  return { type: 'none' };
}
